/**
 * Rate limit state persistence using localStorage.
 * Stores the timestamp when the rate limit expires, allowing the app to
 * proactively block requests before they fail with 429.
 *
 * Supports two independent tiers:
 * - "interactive": chat, sketch, genx-preset-builder, and other user-facing tasks
 * - "background": improve_media_agent
 */

export type RateLimitPriority = 'interactive' | 'background';

const RATE_LIMIT_KEY_INTERACTIVE = 'llm_rate_limit_until_interactive';
const RATE_LIMIT_KEY_BACKGROUND = 'llm_rate_limit_until_background';
const OLD_RATE_LIMIT_KEY = 'llm_rate_limit_until';

const BACKGROUND_TASKS = new Set(['improve_media_agent']);

/**
 * Returns the rate limit priority tier for a given task name.
 */
export function getTaskPriority(task: string | undefined): RateLimitPriority {
    if (task && BACKGROUND_TASKS.has(task)) {
        return 'background';
    }
    return 'interactive';
}

function storageAvailable(): boolean {
    try {
        const storage = window.localStorage;
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    } catch (e) {
        return false;
    }
}

function getKeyForPriority(priority: RateLimitPriority): string {
    return priority === 'background' ? RATE_LIMIT_KEY_BACKGROUND : RATE_LIMIT_KEY_INTERACTIVE;
}

/**
 * Migrate from the old single-key format to the new priority-based keys.
 * Moves the old key value to the interactive tier and removes the old key.
 */
function migrateOldKey(): void {
    if (!storageAvailable()) return;
    const oldValue = localStorage.getItem(OLD_RATE_LIMIT_KEY);
    if (oldValue) {
        localStorage.setItem(RATE_LIMIT_KEY_INTERACTIVE, oldValue);
        localStorage.removeItem(OLD_RATE_LIMIT_KEY);
    }
}

// Run migration on module load
migrateOldKey();

/**
 * Store the rate limit expiration timestamp.
 * @param retryAfterSeconds - Number of seconds until the user can retry
 * @param priority - Which tier to rate limit (defaults to 'interactive')
 */
export function setRateLimit(
    retryAfterSeconds: number,
    priority: RateLimitPriority = 'interactive',
): void {
    if (!storageAvailable()) return;
    const until = Date.now() + retryAfterSeconds * 1000;
    localStorage.setItem(getKeyForPriority(priority), String(until));
}

/**
 * Get the timestamp when the rate limit expires, or null if not rate limited.
 * Automatically clears expired rate limits.
 * @param priority - Which tier to check (defaults to 'interactive')
 */
export function getRateLimitUntil(priority: RateLimitPriority = 'interactive'): number | null {
    if (!storageAvailable()) return null;

    const key = getKeyForPriority(priority);
    const until = localStorage.getItem(key);
    if (!until) return null;

    const timestamp = parseInt(until, 10);
    if (isNaN(timestamp)) {
        localStorage.removeItem(key);
        return null;
    }

    if (Date.now() >= timestamp) {
        localStorage.removeItem(key);
        return null;
    }

    return timestamp;
}

/**
 * Check if the user is currently rate limited.
 * @param priority - Which tier to check (defaults to 'interactive')
 */
export function isRateLimited(priority: RateLimitPriority = 'interactive'): boolean {
    return getRateLimitUntil(priority) !== null;
}

/**
 * Clear the rate limit state.
 * @param priority - Which tier to clear (defaults to 'interactive')
 */
export function clearRateLimit(priority: RateLimitPriority = 'interactive'): void {
    if (!storageAvailable()) return;
    localStorage.removeItem(getKeyForPriority(priority));
}

/**
 * Get the time remaining until the rate limit expires in milliseconds.
 * Returns null if not rate limited.
 * @param priority - Which tier to check (defaults to 'interactive')
 */
export function getTimeUntilRetry(priority: RateLimitPriority = 'interactive'): number | null {
    const until = getRateLimitUntil(priority);
    if (!until) return null;
    return Math.max(0, until - Date.now());
}

/**
 * Format a user-friendly rate limit message.
 * Shows "Daily usage limit reached" for limits > 1 hour (likely daily limits).
 * @param seconds - Seconds until retry is allowed
 * @param formatTime - Function to format seconds into human-readable string
 */
export function formatRateLimitMessage(
    seconds: number,
    formatTime: (seconds: number) => string,
): string {
    const formattedTime = formatTime(seconds);
    const isLikelyDailyLimit = seconds > 3600;
    return isLikelyDailyLimit
        ? `Daily usage limit reached. Please try again in ${formattedTime}.`
        : `Rate limit exceeded. Please try again in ${formattedTime}.`;
}
